<template>
    <div class="log-setting wrapper" v-loading="loading">
        <proTitle></proTitle>

        <div v-show="view === 'list'" class="list-view-root" ref="listViewRoot">
            <div class="page-head flex-align-between">
                <span></span>
                <el-button type="primary" size="small" icon="el-icon-plus" @click="createSetting">新建日志设置</el-button>
            </div>
            <div class="setting-list" ref="settingList" v-if="list.length">
                <div class="setting-item" v-for="item in list" :key="item.id">
                    <div class="setting-item-left">
                        <div class="setting-item-title">
                            {{ item.name || '未命名' }}
                            <el-tag size="mini" :type="item.type === 2 ? 'warning' : ''"
                                :class="{ 'is-multi': item.type === 3 }">{{ typeLabel(item.type) }}</el-tag>
                            <el-tag size="mini" :type="item.status === 1 ? 'success' : 'info'">{{ item.status === 1 ?
                                '已启用' : '未启用' }}</el-tag>
                        </div>
                        <div class="setting-item-desc">
                            {{ item.type === 2 ? '已确认' : '作答' }} {{ (item.dates || []).length }} 天
                            · 最少 {{ item.minWordCount || 0 }} 字
                            <template v-if="item.type === 3"> · {{ item.questionCount || 0 }} 题</template>
                            · {{ item.needReview === 1 ? '需批改' : '无需批改' }}
                            · {{ formatGroupText(item) }}
                        </div>
                    </div>
                    <div class="setting-item-right">
                        <div class="setting-item-actions">
                            <el-button size="mini" plain :loading="item._toggling" @click="toggleStatus(item)">
                                {{ item.status === 1 ? '取消启用' : '启用' }}
                            </el-button>
                            <el-button size="mini" plain @click="openDetail(item.id, true)">查看</el-button>
                            <el-button size="mini" plain @click="openDetail(item.id, false)">编辑</el-button>
                            <el-button size="mini" plain @click="removeSetting(item)">删除</el-button>
                        </div>
                        <div class="setting-item-time">更新于 {{ formatUpdateTime(item.updateAt) }}</div>
                    </div>
                </div>
            </div>
            <el-empty v-else-if="!loading" description="暂无日志设置，点击右上角「新建日志设置」"></el-empty>
        </div>

        <div v-show="view === 'detail'">
            <div class="detail-back-bar">
                <div class="detail-back-left">
                    <el-button size="mini" icon="el-icon-arrow-left" @click="onBackToList">返回列表</el-button>
                    <span class="detail-label" v-if="detailLabel">{{ detailLabel }}</span>
                </div>
                <div>
                    <el-button v-if="readonly && editingId" size="mini" @click="readonly = false">编辑</el-button>
                    <el-button v-if="!readonly" type="primary" size="mini" :loading="saving"
                        @click="saveDetail(true)">保存</el-button>
                </div>
            </div>

            <div class="log-detail-shell">
                <div class="log-mode-card">
                    <div class="form-label">日志类型</div>
                    <div class="log-mode-seg">
                        <button type="button" class="log-mode-btn" :class="{ 'is-active': form.type === 1 }"
                            :disabled="readonly" @click="switchType(1)">通用日志</button>
                        <button type="button" class="log-mode-btn" :class="{ 'is-active': form.type === 3 }"
                            :disabled="readonly" @click="switchType(3)">通用日志（多题）</button>
                        <button type="button" class="log-mode-btn" :class="{ 'is-active': form.type === 2 }"
                            :disabled="readonly" @click="switchType(2)">个性日志</button>
                    </div>
                </div>

                <div class="card-block" v-show="form.type === 1 || form.type === 3">
                    <div class="form-group">
                        <label class="form-label">日志名称 <span class="required">*</span></label>
                        <el-input v-model.trim="form.name" maxlength="50" :placeholder="namePlaceholder"
                            :disabled="readonly" @focus="onDefaultFieldFocus('name')"
                            @blur="onDefaultFieldBlur('name')"></el-input>
                    </div>
                    <div class="form-group">
                        <label class="form-label">日志说明 <span class="required" v-if="form.type === 3">*</span></label>
                        <el-input type="textarea" :rows="3" v-model="form.description"
                            :placeholder="descPlaceholder" :disabled="readonly"
                            @focus="onDefaultFieldFocus('description')"
                            @blur="onDefaultFieldBlur('description')"></el-input>
                    </div>
                    <div class="form-group" style="margin-bottom:0">
                        <label class="form-label">适用用户组 <span class="required">*</span></label>
                        <div class="user-group-grid">
                            <label class="user-group-chip" :class="{ 'is-checked': isGroupChecked(group.id), 'is-disabled': readonly }"
                                v-for="group in groupChipOptions" :key="group.id">
                                <input type="checkbox" :value="group.id" :checked="isGroupChecked(group.id)"
                                    :disabled="readonly" @change="toggleGroup(group.id, $event.target.checked)" />
                                {{ group.name }}
                            </label>
                        </div>
                    </div>
                </div>

                <div class="card-block" v-show="form.type === 1 || form.type === 3">
                    <div class="form-group" style="margin-bottom:0">
                        <label class="form-label">日志作答数量 <span class="required">*</span></label>
                        <div class="date-summary-bar">{{ generalDateSummary }}</div>
                        <date-multi-calendar v-model="form.dates" :disabled="readonly"></date-multi-calendar>
                    </div>
                </div>

                <div class="card-block" v-show="form.type === 2">
                    <div class="form-group">
                        <label class="form-label">日志名称 <span class="required">*</span></label>
                        <el-input v-model.trim="form.name" maxlength="50" :placeholder="namePlaceholder"
                            :disabled="readonly" @focus="onDefaultFieldFocus('name')"
                            @blur="onDefaultFieldBlur('name')"></el-input>
                    </div>
                    <div class="form-group" style="margin-bottom:0">
                        <label class="form-label">适用用户组 <span class="required">*</span></label>
                        <div class="user-group-grid">
                            <label class="user-group-chip" :class="{ 'is-checked': isGroupChecked(group.id), 'is-disabled': readonly }"
                                v-for="group in groupChipOptions" :key="'d-' + group.id">
                                <input type="checkbox" :value="group.id" :checked="isGroupChecked(group.id)"
                                    :disabled="readonly" @change="toggleGroup(group.id, $event.target.checked)" />
                                {{ group.name }}
                            </label>
                        </div>
                    </div>
                </div>

                <div class="card-block" v-show="form.type === 2">
                    <div class="form-group" style="margin-bottom:0">
                        <label class="form-label">作答日期 <span class="required">*</span></label>
                        <div class="date-summary-bar">{{ personalDateSummary }}</div>
                        <date-multi-calendar v-model="form.dates" :disabled="readonly">
                            <template slot="actions">
                                <el-button size="mini" type="primary" :disabled="readonly"
                                    @click="confirmDailyDates">确认</el-button>
                                <el-button size="mini" :disabled="readonly" @click="form.dates = []">清空</el-button>
                            </template>
                        </date-multi-calendar>
                    </div>
                </div>

                <div class="card-block">
                    <div class="form-row-2">
                        <div class="form-group">
                            <label class="form-label">提交时间 <span class="required">*</span></label>
                            <div class="time-range-row">
                                <el-date-picker v-model="form.submitStart" type="datetime"
                                    value-format="yyyy-MM-dd HH:mm" format="yyyy-MM-dd HH:mm" placeholder="开始日期时间"
                                    :disabled="readonly" style="width:100%"></el-date-picker>
                                <span class="time-range-sep">至</span>
                                <el-date-picker v-model="form.submitEnd" type="datetime" value-format="yyyy-MM-dd HH:mm"
                                    format="yyyy-MM-dd HH:mm" placeholder="结束日期时间" :disabled="readonly"
                                    style="width:100%"></el-date-picker>
                            </div>
                        </div>
                        <div class="form-group">
                            <label class="form-label">日志最少字数 <span class="required">*</span></label>
                            <el-input-number v-model="form.minWordCount" :min="0" :step="10" :disabled="readonly"
                                controls-position="right" style="width:100%"></el-input-number>
                        </div>
                    </div>
                    <div class="form-group" v-show="form.type !== 3" style="margin-bottom:0">
                        <div class="switch-title-row">
                            <label class="form-label" style="margin-bottom:0">
                                是否允许上传附件
                                <span class="form-hint-inline">开启后学员可上传附件（PDF、Word、图片等）<template
                                        v-if="form.type === 2">；对全部个性日志统一生效</template></span>
                            </label>
                            <el-switch v-model="form.allowAttachment" :active-value="1" :inactive-value="0"
                                :disabled="readonly"></el-switch>
                        </div>
                    </div>
                </div>

                <div class="card-block" v-show="form.type === 2">
                    <div class="card-header">
                        <h3>每日日志配置（共 {{ form.confirmedDates.length }} 天）</h3>
                        <el-button size="mini" :disabled="readonly || !form.confirmedDates.length"
                            @click="copyDailyPrompts">批量复制提示语</el-button>
                    </div>
                    <div v-if="!form.confirmedDates.length" class="daily-empty">请先选择作答日期并点击「确认」，系统将生成对应天数的日志配置</div>
                    <div class="day-setting-list" v-else>
                        <div class="day-setting-card" v-for="(dk, index) in form.confirmedDates" :key="dk">
                            <div class="day-setting-header" @click="toggleDayOpen(dk)">
                                <div class="day-num">{{ index + 1 }}</div>
                                <div class="day-info">
                                    <div class="day-name">{{ (form.dayConfigs[dk] && form.dayConfigs[dk].name) || ('第' +
                                        (index + 1) + '天')
                                        }}</div>
                                    <div class="day-meta">{{ formatDateLabel(dk) }} · {{ weekdayLabel(dk) }}</div>
                                </div>
                                <i class="el-icon-arrow-right day-toggle-icon" :class="{ 'is-open': openDays[dk] }"></i>
                            </div>
                            <div class="day-setting-body" v-show="openDays[dk]">
                                <div class="form-group">
                                    <label class="form-label">当日名称 <span class="required">*</span></label>
                                    <el-input v-model.trim="form.dayConfigs[dk].name" maxlength="50" placeholder="请输入当日日志名称"
                                        :disabled="readonly"></el-input>
                                </div>
                                <div class="form-group" style="margin-bottom:0">
                                    <label class="form-label">当日说明</label>
                                    <el-input type="textarea" :rows="3" v-model="form.dayConfigs[dk].description"
                                        placeholder="请输入当日提示语" :disabled="readonly"></el-input>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="card-block" v-show="form.type === 3">
                    <div class="multi-q-toolbar">
                        <div>
                            <label class="form-label">题目配置 <span class="required">*</span></label>
                            <div class="multi-q-hint">可添加多道题目；添加时需选择题型（填空题 / 简答题 / 单选题 / 多选题）。</div>
                        </div>
                    </div>
                    <div class="multi-q-list">
                        <div class="multi-q-card" v-for="(q, qIndex) in form.questions" :key="q._key">
                            <div class="multi-q-card-head">
                                <div class="multi-q-head-left">
                                    <span class="multi-q-index">第{{ chineseNumber(qIndex + 1) }}题</span>
                                    <span class="multi-q-type-tag">{{ questionTypeName(q.type) }}</span>
                                </div>
                                <div class="multi-q-actions">
                                    <button type="button" class="q-icon-btn" title="上移"
                                        :disabled="readonly || qIndex === 0" @click="moveQuestion(qIndex, -1)">↑</button>
                                    <button type="button" class="q-icon-btn" title="下移"
                                        :disabled="readonly || qIndex === form.questions.length - 1"
                                        @click="moveQuestion(qIndex, 1)">↓</button>
                                    <button type="button" class="q-icon-btn is-danger" title="删除本题"
                                        :disabled="readonly || form.questions.length <= 1"
                                        @click="removeQuestion(qIndex)">×</button>
                                </div>
                            </div>
                            <div class="multi-q-card-body">
                                <template v-if="q.type === 1">
                                    <div class="multi-blank-list">
                                        <div class="multi-blank-row is-indexed" v-for="(blank, bIndex) in q.blanks" :key="blank._key">
                                            <span class="multi-blank-index">({{ itemSerial(qIndex, bIndex) }})</span>
                                            <div class="multi-blank-fields">
                                                <div class="form-group">
                                                    <label class="form-label">标题 <span class="required">*</span></label>
                                                    <el-input v-model="blank.label" maxlength="30" placeholder="如：优点、不足、收获"
                                                        :disabled="readonly"></el-input>
                                                </div>
                                                <div class="form-group">
                                                    <label class="form-label">填写提示</label>
                                                    <el-input v-model="blank.hint" maxlength="100" placeholder="选填，展示在作答空内"
                                                        :disabled="readonly"></el-input>
                                                </div>
                                                <div class="form-group">
                                                    <label class="form-label">输入设置</label>
                                                    <el-select v-model="blank.inputRule" :disabled="readonly" style="width:100%">
                                                        <el-option :value="0" label="无限制"></el-option>
                                                        <el-option :value="1" label="只允许填入数字"></el-option>
                                                    </el-select>
                                                </div>
                                                <div class="multi-blank-actions">
                                                    <button type="button" class="q-icon-btn is-danger" title="删除此小题"
                                                        :disabled="readonly || q.blanks.length <= 1"
                                                        @click="removeBlank(qIndex, bIndex)">×</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="multi-q-add-line">
                                        <el-button size="mini" :disabled="readonly || q.blanks.length >= 20"
                                            @click="addBlank(qIndex)">＋ 添加小题</el-button>
                                    </div>
                                </template>
                                <template v-else-if="q.type === 2">
                                    <div class="multi-blank-row is-indexed" v-if="q.blanks[0]">
                                        <span class="multi-blank-index">({{ itemSerial(qIndex, 0) }})</span>
                                        <div class="multi-blank-fields is-essay">
                                            <div class="form-group">
                                                <label class="form-label">标题 <span class="required">*</span></label>
                                                <el-input v-model="q.blanks[0].label" maxlength="30" placeholder="如：整体反思"
                                                    :disabled="readonly"></el-input>
                                            </div>
                                            <div class="form-group">
                                                <label class="form-label">填写提示</label>
                                                <el-input v-model="q.blanks[0].hint" maxlength="100" placeholder="选填，展示给学员"
                                                    :disabled="readonly"></el-input>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <template v-else>
                                    <div class="multi-blank-row is-indexed">
                                        <span class="multi-blank-index">({{ itemSerial(qIndex, 0) }})</span>
                                        <div class="form-group">
                                            <label class="form-label">题干</label>
                                            <el-input v-model="q.prompt" maxlength="120"
                                                placeholder="选填，如：以下哪项更符合你的收获？" :disabled="readonly"></el-input>
                                        </div>
                                    </div>
                                    <div class="form-group" style="margin-bottom:8px">
                                        <label class="form-label">选项 <span class="required">*</span></label>
                                    </div>
                                    <div class="multi-option-list">
                                        <div class="multi-option-row" v-for="(opt, oIndex) in q.options" :key="opt._key">
                                            <span class="multi-option-index">{{ optionLetter(oIndex) }}</span>
                                            <el-input v-model="opt.text" maxlength="100" placeholder="请输入选项内容"
                                                :disabled="readonly"></el-input>
                                            <button type="button" class="q-icon-btn is-danger" title="删除选项"
                                                :disabled="readonly || q.options.length <= 2"
                                                @click="removeOption(qIndex, oIndex)">×</button>
                                        </div>
                                    </div>
                                    <div class="multi-q-add-line">
                                        <el-button size="mini" :disabled="readonly || q.options.length >= 20"
                                            @click="addOption(qIndex)">＋ 添加选项</el-button>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                    <div class="multi-q-add-wrap">
                        <el-button size="mini" type="primary" :disabled="readonly || form.questions.length >= 50"
                            @click="openQuestionTypeDialog">＋ 添加题目</el-button>
                    </div>
                </div>

                <div class="card-block">
                    <div class="form-group" :style="{ marginBottom: form.needReview === 1 ? '16px' : '0' }">
                        <div class="switch-title-row">
                            <label class="form-label" style="margin-bottom:0">
                                是否需要批改
                                <span class="form-hint-inline">开启后需指定批改人，对学员提交的日志进行批改</span>
                            </label>
                            <el-switch v-model="form.needReview" :active-value="1" :inactive-value="0"
                                :disabled="readonly" @change="onNeedReviewChange"></el-switch>
                        </div>
                    </div>
                    <div class="form-group" v-if="form.needReview === 1" style="margin-bottom:0">
                        <label class="form-label">
                            批改人 <span class="required">*</span>
                            <span class="form-hint-inline">仅可选择上方已勾选的适用用户组，用户组和批改人均可多选</span>
                        </label>
                        <div class="form-row-2" style="margin-bottom:0">
                            <el-select v-model="form.reviewUserGroupId"
                                :placeholder="reviewerGroupPlaceholder"
                                :disabled="readonly || !reviewerGroupOptions.length"
                                filterable style="width:100%" @change="onReviewerGroupChange">
                                <el-option v-for="g in reviewerGroupOptions" :key="g.id" :label="g.name"
                                    :value="g.id"></el-option>
                            </el-select>
                            <el-select v-model="form.reviewUserId" placeholder="请先选择用户组"
                                :disabled="readonly || !form.reviewUserGroupId" filterable style="width:100%">
                                <el-option v-for="u in reviewerUsers" :key="u.id" :label="u.name"
                                    :value="u.id"></el-option>
                            </el-select>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 离开编辑页确认弹窗 -->
        <el-dialog
            title="提示"
            :visible.sync="leaveDialogVisible"
            width="420px"
            custom-class="log-leave-dialog"
            :close-on-click-modal="false"
            append-to-body>
            <div class="leave-dialog-body">还未保存数据，是否保存？</div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="confirmLeaveWithoutSave">否</el-button>
                <el-button type="primary" :loading="saving" @click="confirmLeaveWithSave">是</el-button>
            </span>
        </el-dialog>

        <!-- 启用/取消启用/删除确认弹窗 -->
        <el-dialog
            :visible.sync="actionDialogVisible"
            width="400px"
            custom-class="log-action-dialog"
            :show-close="true"
            :close-on-click-modal="false"
            append-to-body>
            <div class="action-dialog-body">
                <div class="action-dialog-question">{{ actionDialogData.question }}</div>
                <div class="action-dialog-name">「{{ actionDialogData.name }}」</div>
                <div class="action-dialog-hint">{{ actionDialogData.hint }}</div>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="actionDialogVisible = false">{{ actionDialogData.cancelText }}</el-button>
                <el-button type="primary" :loading="actionDialogData.loading" @click="confirmActionDialog">{{ actionDialogData.confirmText }}</el-button>
            </span>
        </el-dialog>

        <el-dialog title="选择题型" :visible.sync="typeDialogVisible" width="480px" custom-class="log-q-type-dialog"
            :close-on-click-modal="false" append-to-body>
            <div class="q-type-hint">请选择要添加的题目类型，确认后进入题目配置。</div>
            <div class="q-type-grid">
                <button type="button" class="q-type-option" v-for="item in questionTypes" :key="item.type"
                    :class="{ 'is-selected': pendingQuestionType === item.type }"
                    @click="pendingQuestionType = item.type">
                    <span class="q-type-name">{{ item.name }}</span>
                    <span class="q-type-desc">{{ item.desc }}</span>
                </button>
            </div>
            <span slot="footer">
                <el-button size="small" @click="typeDialogVisible = false">取消</el-button>
                <el-button size="small" type="primary" @click="confirmAddQuestion">确定添加</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import DateMultiCalendar from './components/date-multi-calendar.vue';
    import {
        ALL_GROUP_ID,
        isApiSuccess,
        apiMsg,
        apiData,
        displayGroupNames,
        formatDateLabel,
        weekdayLabel,
        getContiguousSegments,
        defaultSubmitRange,
        isAllGroups,
        normalizeUserGroupIds,
    } from './log-moment-shared.js';

    const DEFAULT_NAME = '研修日志';
    const DEFAULT_DESC = '请记录研修过程中的学习收获、思考与感悟。';
    const MAX_QUESTIONS = 50;
    const MAX_BLANKS = 20;
    const MAX_OPTIONS = 20;

    const QUESTION_TYPES = [
        { type: 1, name: '填空题', desc: '可配置多个小题，每个小题有标题、填写提示与输入设置' },
        { type: 2, name: '简答题', desc: '学员按标题填写一段文字作答' },
        { type: 3, name: '单选题', desc: '配置选项，学员只能选一项' },
        { type: 4, name: '多选题', desc: '配置选项，学员可选多项' },
    ];

    let localKeySeq = 1;
    const nextKey = (prefix) => `${prefix}-${localKeySeq++}`;

    const emptyBlank = (partial = {}) => ({
        id: partial.id,
        _key: partial._key || nextKey('b'),
        label: partial.label || '',
        hint: partial.hint || '',
        inputRule: Number(partial.inputRule) === 1 ? 1 : 0,
    });

    const emptyOption = (partial = {}) => ({
        id: partial.id,
        _key: partial._key || nextKey('o'),
        text: partial.text || '',
    });

    const createQuestion = (type, partial = {}) => {
        const q = {
            id: partial.id,
            _key: partial._key || nextKey('q'),
            type,
        };
        if (type === 1) {
            q.blanks = (partial.blanks && partial.blanks.length)
                ? partial.blanks.map((b) => emptyBlank(b))
                : [emptyBlank()];
        } else if (type === 2) {
            q.blanks = [emptyBlank((partial.blanks && partial.blanks[0]) || {})];
        } else {
            q.prompt = partial.prompt || '';
            q.options = (partial.options && partial.options.length)
                ? partial.options.map((o) => emptyOption(o))
                : [emptyOption(), emptyOption()];
        }
        return q;
    };

    const defaultQuestions = () => [
        createQuestion(1, {
            blanks: [
                { label: '主要收获', hint: '请填写今日主要收获' },
                { label: '存在问题', hint: '请填写今日遇到的问题或困惑' },
                { label: '改进思考', hint: '请填写改进想法与下一步计划' },
            ],
        }),
        createQuestion(2, {
            blanks: [{ label: '整体反思', hint: '请写下今日整体思考与感悟' }],
        }),
    ];

    const mapQuestionsFromApi = (list) => (list || []).map((q) => {
        const type = Number(q.type) || 1;
        return createQuestion(type, {
            id: q.id,
            prompt: q.prompt,
            blanks: q.blanks,
            options: q.options,
        });
    });

    const emptyForm = () => ({
        type: 1,
        name: DEFAULT_NAME,
        description: DEFAULT_DESC,
        userGroupIds: [ALL_GROUP_ID],
        dates: [],
        confirmedDates: [],
        dayConfigs: {},
        questions: [],
        submitStart: '',
        submitEnd: '',
        minWordCount: 100,
        allowAttachment: 0,
        needReview: 0,
        reviewUserGroupId: null,
        reviewUserId: null,
        status: 0,
    });

    export default {
        components: { DateMultiCalendar },
        data() {
            return {
                loading: false,
                saving: false,
                view: 'list',
                readonly: false,
                editingId: null,
                list: [],
                userGroupOptions: [],
                reviewerUsers: [],
                openDays: {},
                form: emptyForm(),
                originalForm: null,
                questionTypes: QUESTION_TYPES,
                typeDialogVisible: false,
                pendingQuestionType: 1,
                namePlaceholder: '请输入日志名称',
                descPlaceholder: '请输入日志说明，将展示给学员参考',
                formSnapshot: '',
                leaveDialogVisible: false,
                actionDialogVisible: false,
                actionDialogData: {
                    question: '',
                    name: '',
                    hint: '',
                    cancelText: '',
                    confirmText: '',
                    loading: false,
                    callback: null,
                },
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            projectId() {
                return this.currentProject && this.currentProject.id;
            },
            groupChipOptions() {
                return [{ id: ALL_GROUP_ID, name: '全部学员' }].concat(this.userGroupOptions);
            },
            detailLabel() {
                if (!this.editingId) return '';
                return (this.readonly ? '查看：' : '编辑：') + (this.form.name || '日志设置');
            },
            generalDateSummary() {
                let n = (this.form.dates || []).length;
                let text = this.formatDatesSummary(this.form.dates);
                if (!n) return '共0天。学员需完成同等天数的作答';
                return `${text}，共${n}天。学员需完成同等天数的作答`;
            },
            personalDateSummary() {
                let n = (this.form.dates || []).length;
                let text = this.formatDatesSummary(this.form.dates);
                if (!n) return '共0天。确认后关联下方日志';
                return `${text}，共${n}天。确认后关联下方日志`;
            },
            reviewerGroupOptions() {
                let ids = (this.form.userGroupIds || []).map(Number);
                if (!ids.length) return [];
                if (isAllGroups(ids, this.userGroupOptions)) return this.userGroupOptions;
                let set = {};
                ids.forEach((id) => { set[id] = true; });
                return this.userGroupOptions.filter((g) => set[g.id]);
            },
            reviewerGroupPlaceholder() {
                if (!(this.form.userGroupIds || []).length) return '请先选择适用用户组';
                return '请选择用户组';
            },
            // 每道题第一个作答项的全局序号，题目顺序或小题数量变化时自动重算
            blankStartIndexes() {
                let list = this.form.questions || [];
                let starts = [];
                let cursor = 1;
                list.forEach((q) => {
                    starts.push(cursor);
                    cursor += this.questionItemCount(q);
                });
                return starts;
            },
        },
        watch: {
            projectId: {
                immediate: true,
                handler(id) {
                    if (id) this.loadAll();
                },
            },
            'form.dates'(val) {
                if (this.view !== 'detail' || this.readonly || this.editingId) return;
                if (!val || !val.length) return;
                if (!this.form.submitStart || !this.form.submitEnd) {
                    let range = defaultSubmitRange(val);
                    this.form.submitStart = range.start;
                    this.form.submitEnd = range.end;
                }
            },
            'form.userGroupIds': {
                deep: true,
                handler() {
                    this.syncReviewerWithGroups();
                },
            },
            view(val) {
                if (val === 'list') {
                    this.$nextTick(() => this.syncListHeight());
                } else {
                    let root = this.$refs.listViewRoot;
                    if (root) root.style.height = '';
                }
            },
        },
        methods: {
            displayGroupNames,
            formatDateLabel,
            weekdayLabel,
            formatGroupText(item) {
                let names = displayGroupNames(item, this.userGroupOptions);
                if (!names || !names.length) return '未配置用户组';
                if (isAllGroups(item && item.userGroupIds, this.userGroupOptions, item)) return '适用全部学员';
                if (names.length === 1) return `适用${names[0]}`;
                if (names.length === 2) return `适用${names[0]}、${names[1]}`;
                return `适用${names[0]}等${names.length}个用户组`;
            },
            formatUpdateTime(val) {
                if (!val) return '-';
                let s = String(val);
                if (s.length >= 16) return s.substring(0, 16);
                return s;
            },
            syncListHeight() {
                let sidebar = document.querySelector('.sidebar');
                if (!sidebar) return;
                let root = this.$refs.listViewRoot;
                if (!root) return;
                root.style.height = sidebar.offsetHeight + 'px';
                root.style.overflowY = 'auto';
                root.style.boxSizing = 'border-box';
            },
            formatDatesSummary(dates) {
                return getContiguousSegments(dates).map((seg) => {
                    if (seg.start === seg.end) return formatDateLabel(seg.start);
                    return `${formatDateLabel(seg.start)}至${formatDateLabel(seg.end)}`;
                }).join('，');
            },
            normalizeForCompare(form) {
                let dayConfigs = {};
                Object.keys(form.dayConfigs || {}).sort().forEach((k) => {
                    let cfg = form.dayConfigs[k] || {};
                    dayConfigs[k] = {
                        name: cfg.name || '',
                        description: cfg.description || '',
                    };
                });
                return {
                    type: form.type,
                    name: form.name || '',
                    description: form.description || '',
                    userGroupIds: (form.userGroupIds || []).map(Number).slice().sort((a, b) => a - b),
                    dates: (form.dates || []).slice().sort(),
                    confirmedDates: (form.confirmedDates || []).slice().sort(),
                    questions: this.normalizeQuestions(form.questions),
                    dayConfigs,
                    submitStart: form.submitStart || '',
                    submitEnd: form.submitEnd || '',
                    minWordCount: form.minWordCount,
                    allowAttachment: form.allowAttachment,
                    needReview: form.needReview,
                    reviewUserGroupId: form.reviewUserGroupId,
                    reviewUserId: form.reviewUserId,
                };
            },
            takeSnapshot() {
                this.formSnapshot = JSON.stringify(this.normalizeForCompare(this.form));
            },
            isDirty() {
                return this.formSnapshot !== JSON.stringify(this.normalizeForCompare(this.form));
            },
            isGroupChecked(id) {
                if (Number(id) === ALL_GROUP_ID) {
                    return isAllGroups(this.form.userGroupIds, this.userGroupOptions);
                }
                return (this.form.userGroupIds || []).some((x) => Number(x) === Number(id));
            },
            toggleGroup(id, checked) {
                let numId = Number(id);
                if (numId === ALL_GROUP_ID) {
                    this.form.userGroupIds = checked ? [ALL_GROUP_ID] : [];
                    return;
                }
                let wasAll = isAllGroups(this.form.userGroupIds, this.userGroupOptions);
                if (wasAll) {
                    this.form.userGroupIds = [numId];
                    return;
                }
                let ids = (this.form.userGroupIds || []).map(Number).filter((x) => x !== ALL_GROUP_ID);
                if (checked) {
                    if (ids.indexOf(numId) < 0) ids.push(numId);
                } else {
                    ids = ids.filter((x) => x !== numId);
                }
                if (this.userGroupOptions.length && ids.length >= this.userGroupOptions.length &&
                    this.userGroupOptions.every((g) => ids.indexOf(Number(g.id)) >= 0)) {
                    this.form.userGroupIds = [ALL_GROUP_ID];
                } else {
                    this.form.userGroupIds = ids;
                }
            },
            onDefaultFieldFocus(field) {
                if (this.readonly) return;
                if (field === 'name') {
                    if (this.form.name === DEFAULT_NAME) {
                        this.form.name = '';
                        this.namePlaceholder = DEFAULT_NAME;
                    }
                } else if (field === 'description') {
                    if (this.form.description === DEFAULT_DESC) {
                        this.form.description = '';
                        this.descPlaceholder = DEFAULT_DESC;
                    }
                }
            },
            onDefaultFieldBlur(field) {
                if (this.readonly) return;
                if (field === 'name') {
                    if (!(this.form.name || '').trim()) {
                        this.form.name = DEFAULT_NAME;
                        this.namePlaceholder = '请输入日志名称';
                    }
                } else if (field === 'description') {
                    if (!(this.form.description || '').trim()) {
                        this.form.description = DEFAULT_DESC;
                        this.descPlaceholder = '请输入日志说明，将展示给学员参考';
                    }
                }
            },
            typeLabel(type) {
                if (type === 2) return '个性日志';
                if (type === 3) return '通用日志（多题）';
                return '通用日志';
            },
            questionTypeName(type) {
                let found = QUESTION_TYPES.find((item) => item.type === type);
                return found ? found.name : '填空题';
            },
            optionLetter(index) {
                return String.fromCharCode(65 + index);
            },
            questionItemCount(q) {
                if (!q) return 0;
                if (q.type === 1) return (q.blanks && q.blanks.length) || 0;
                return 1;
            },
            itemSerial(qIndex, bIndex) {
                let start = this.blankStartIndexes[qIndex] || 1;
                return start + (bIndex || 0);
            },
            chineseNumber(n) {
                const digits = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
                let num = Number(n);
                if (!num || num < 1) return '';
                if (num < 10) return digits[num];
                if (num < 20) return num === 10 ? '十' : `十${digits[num % 10]}`;
                if (num < 100) {
                    let tens = Math.floor(num / 10);
                    let ones = num % 10;
                    return `${digits[tens]}十${ones ? digits[ones] : ''}`;
                }
                return String(num);
            },
            normalizeQuestions(list) {
                return (list || []).map((q) => {
                    if (q.type === 1 || q.type === 2) {
                        return {
                            id: q.id || null,
                            type: q.type,
                            blanks: (q.blanks || []).map((b) => ({
                                id: b.id || null,
                                label: b.label || '',
                                hint: b.hint || '',
                                inputRule: q.type === 1 ? (Number(b.inputRule) === 1 ? 1 : 0) : 0,
                            })),
                        };
                    }
                    return {
                        id: q.id || null,
                        type: q.type,
                        prompt: q.prompt || '',
                        options: (q.options || []).map((o) => ({
                            id: o.id || null,
                            text: o.text || '',
                        })),
                    };
                });
            },
            switchType(type) {
                if (this.readonly || this.form.type === type) return;
                if (this.editingId && this.originalForm) {
                    let restored = JSON.parse(JSON.stringify(this.originalForm));
                    restored.type = type;
                    this.form = restored;
                    this.openDays = {};
                    if (type === 2 && this.form.confirmedDates && this.form.confirmedDates[0]) {
                        this.$set(this.openDays, this.form.confirmedDates[0], true);
                    }
                    if (type === 3) this.ensureMultiQuestions();
                } else {
                    this.form = emptyForm();
                    this.form.type = type;
                    if (type === 2) this.form.description = '';
                    if (type === 3) this.ensureMultiQuestions();
                    this.openDays = {};
                    this.reviewerUsers = [];
                    this.namePlaceholder = '请输入日志名称';
                    this.descPlaceholder = '请输入日志说明，将展示给学员参考';
                    this.takeSnapshot();
                }
            },
            ensureMultiQuestions() {
                this.form.allowAttachment = 0;
                if (!this.form.questions) this.$set(this.form, 'questions', []);
                if (!this.form.questions.length) this.form.questions = defaultQuestions();
            },
            async loadAll() {
                if (!this.projectId) return;
                this.loading = true;
                try {
                    await Promise.all([this.fetchProjectInfo(), this.fetchList()]);
                } finally {
                    this.loading = false;
                    if (this.view === 'list') {
                        this.$nextTick(() => this.syncListHeight());
                    }
                }
            },
            async fetchProjectInfo() {
                let res = await this.$Api.StageV2.getProjectInfo({ projectId: this.projectId });
                if (!isApiSuccess(res)) return;
                let data = apiData(res) || {};
                this.userGroupOptions = (data.availableUserGroups || []).map((g) => ({
                    id: Number(g.id),
                    name: g.name,
                }));
            },
            async fetchList() {
                let res = await this.$Api.Log.getTemplateList({ projectId: this.projectId });
                if (!isApiSuccess(res)) {
                    this.$message.error(apiMsg(res) || '获取日志设置失败');
                    this.list = [];
                    return;
                }
                this.list = apiData(res) || [];
            },
            resetForm() {
                this.form = emptyForm();
                this.originalForm = null;
                this.openDays = {};
                this.reviewerUsers = [];
                this.namePlaceholder = '请输入日志名称';
                this.descPlaceholder = '请输入日志说明，将展示给学员参考';
            },
            createSetting() {
                this.editingId = null;
                this.readonly = false;
                this.resetForm();
                this.view = 'detail';
                this.$nextTick(() => this.takeSnapshot());
            },
            async openDetail(id, readonly) {
                this.loading = true;
                try {
                    let res = await this.$Api.Log.getTemplateDetail({ id });
                    if (!isApiSuccess(res)) {
                        this.$message.error(apiMsg(res) || '获取详情失败');
                        return;
                    }
                    let data = apiData(res) || {};
                    let dates = (data.days || []).map((d) => d.logDate).filter(Boolean);
                    let dayConfigs = {};
                    (data.days || []).forEach((d) => {
                        dayConfigs[d.logDate] = {
                            name: d.name || '',
                            description: d.description || '',
                        };
                    });
                    this.editingId = id;
                    this.readonly = !!readonly;
                    this.form = {
                        type: data.type || 1,
                        name: data.name || '',
                        description: data.description || '',
                        userGroupIds: normalizeUserGroupIds((data.userGroupIds || []).map(Number), this.userGroupOptions, data),
                        dates: dates.slice(),
                        confirmedDates: data.type === 2 ? dates.slice() : [],
                        dayConfigs,
                        questions: data.type === 3 ? mapQuestionsFromApi(data.questions) : [],
                        submitStart: data.submitStart || '',
                        submitEnd: data.submitEnd || '',
                        minWordCount: data.minWordCount || 0,
                        allowAttachment: data.type === 3 ? 0 : (data.allowAttachment == null ? 1 : data.allowAttachment),
                        needReview: data.needReview || 0,
                        reviewUserGroupId: data.reviewUserGroupId || null,
                        reviewUserId: data.reviewUserId || null,
                        status: data.status || 0,
                    };
                    this.originalForm = JSON.parse(JSON.stringify(this.form));
                    this.openDays = {};
                    if (this.form.confirmedDates[0]) this.$set(this.openDays, this.form.confirmedDates[0], true);
                    if (this.form.reviewUserGroupId) await this.fetchReviewerUsers();
                    this.view = 'detail';
                    this.$nextTick(() => this.takeSnapshot());
                } finally {
                    this.loading = false;
                }
            },
            onBackToList() {
                if (this.readonly || !this.isDirty()) {
                    this.backToList();
                    return;
                }
                this.leaveDialogVisible = true;
            },
            confirmLeaveWithoutSave() {
                this.leaveDialogVisible = false;
                this.backToList();
            },
            async confirmLeaveWithSave() {
                let ok = await this.saveDetail(true);
                if (ok) this.leaveDialogVisible = false;
            },
            backToList() {
                this.leaveDialogVisible = false;
                this.view = 'list';
                this.editingId = null;
                this.readonly = false;
                this.originalForm = null;
                this.formSnapshot = '';
                this.fetchList();
            },
            confirmDailyDates() {
                let dates = (this.form.dates || []).slice().sort();
                if (!dates.length) {
                    this.$message.warning('请先选择作答日期');
                    return;
                }
                let configs = { ...this.form.dayConfigs };
                dates.forEach((dk, index) => {
                    if (!configs[dk]) {
                        configs[dk] = { name: `第${index + 1}天`, description: '' };
                    }
                });
                Object.keys(configs).forEach((dk) => {
                    if (dates.indexOf(dk) < 0) delete configs[dk];
                });
                this.form.dayConfigs = configs;
                this.form.confirmedDates = dates;
                this.openDays = {};
                if (dates[0]) this.$set(this.openDays, dates[0], true);
                this.$message.success(`已确认 ${dates.length} 天作答日期`);
            },
            toggleDayOpen(dk) {
                if (this.openDays[dk]) {
                    this.openDays = {};
                    return;
                }
                this.openDays = {};
                this.$set(this.openDays, dk, true);
            },
            copyDailyPrompts() {
                let first = this.form.confirmedDates[0];
                let text = first && this.form.dayConfigs[first] ? this.form.dayConfigs[first].description : '';
                if (!text) {
                    this.$message.warning('第一天尚未填写提示语');
                    return;
                }
                this.form.confirmedDates.forEach((dk) => {
                    if (this.form.dayConfigs[dk]) this.form.dayConfigs[dk].description = text;
                });
                this.$message.success('已复制第一天提示语到全部天数');
            },
            onNeedReviewChange(val) {
                if (!val) {
                    this.form.reviewUserGroupId = null;
                    this.form.reviewUserId = null;
                    this.reviewerUsers = [];
                }
            },
            syncReviewerWithGroups() {
                if (!this.form.reviewUserGroupId) return;
                let allowed = this.reviewerGroupOptions.some((g) => Number(g.id) === Number(this.form.reviewUserGroupId));
                if (!allowed) {
                    this.form.reviewUserGroupId = null;
                    this.form.reviewUserId = null;
                    this.reviewerUsers = [];
                }
            },
            async onReviewerGroupChange() {
                this.form.reviewUserId = null;
                await this.fetchReviewerUsers();
            },
            async fetchReviewerUsers() {
                this.reviewerUsers = [];
                if (!this.form.reviewUserGroupId) return;
                let types = ['leader', 'expert', 'assistant', 'teacher'];
                let map = {};
                try {
                    let results = await Promise.all(types.map((type) =>
                        this.$Api.Course.TeacherList({
                            userGroupIds: [this.form.reviewUserGroupId],
                            type,
                        })
                    ));
                    results.forEach((res) => {
                        let list = (res && res.data) || [];
                        list.forEach((u) => {
                            if (u && u.id != null && !map[u.id]) map[u.id] = { id: u.id, name: u.name || String(u.id) };
                        });
                    });
                } catch (e) { /* ignore */ }
                this.reviewerUsers = Object.keys(map).map((k) => map[k]);
            },
            buildPayload() {
                if (!this.form.userGroupIds.length) {
                    this.$message.warning('请至少选择一个适用用户组');
                    return null;
                }
                if (!this.form.name) {
                    this.$message.warning('请填写日志名称');
                    return null;
                }
                if (this.form.type === 3 && !(this.form.description || '').trim()) {
                    this.$message.warning('请填写日志说明');
                    return null;
                }
                let dates = this.form.type === 2 ? this.form.confirmedDates : this.form.dates;
                if (!dates.length) {
                    this.$message.warning(this.form.type === 2 ? '请先确认作答日期' : '请在日历中选择作答日期');
                    return null;
                }
                if (!this.form.submitStart || !this.form.submitEnd) {
                    this.$message.warning('请设置提交时间');
                    return null;
                }
                if (this.form.submitStart > this.form.submitEnd) {
                    this.$message.warning('开始日期时间不能晚于结束日期时间');
                    return null;
                }
                if (this.form.minWordCount == null || this.form.minWordCount < 0) {
                    this.$message.warning('请填写有效的最少字数');
                    return null;
                }
                if (this.form.needReview === 1 && (!this.form.reviewUserGroupId || !this.form.reviewUserId)) {
                    this.$message.warning('请选择批改人');
                    return null;
                }
                let days = dates.slice().sort().map((dk) => {
                    let item = { logDate: dk };
                    if (this.form.type === 2) {
                        let cfg = this.form.dayConfigs[dk] || {};
                        item.name = (cfg.name || '').trim();
                        item.description = cfg.description || '';
                    }
                    return item;
                });
                if (this.form.type === 2) {
                    let miss = days.find((d) => !d.name);
                    if (miss) {
                        this.$message.warning('请填写每日日志名称');
                        return null;
                    }
                }
                let questions = null;
                if (this.form.type === 3) {
                    questions = this.collectQuestions();
                    if (!questions) return null;
                }
                let payload = {
                    type: this.form.type,
                    name: this.form.name,
                    description: this.form.type === 2 ? '' : (this.form.description || ''),
                    projectId: this.projectId,
                    userGroupIds: this.form.userGroupIds.map(Number),
                    days,
                    submitStart: this.form.submitStart,
                    submitEnd: this.form.submitEnd,
                    minWordCount: this.form.minWordCount,
                    allowAttachment: this.form.type === 3 ? 0 : this.form.allowAttachment,
                    needReview: this.form.needReview,
                    reviewUserGroupId: this.form.needReview === 1 ? this.form.reviewUserGroupId : null,
                    reviewUserId: this.form.needReview === 1 ? this.form.reviewUserId : null,
                    status: this.editingId ? this.form.status : 0,
                };
                if (questions) payload.questions = questions;
                if (this.editingId) payload.id = this.editingId;
                return payload;
            },
            collectQuestions() {
                let list = this.form.questions || [];
                if (!list.length) {
                    this.$message.warning('请至少添加 1 道题目');
                    return null;
                }
                if (list.length > MAX_QUESTIONS) {
                    this.$message.warning(`最多添加 ${MAX_QUESTIONS} 道题目`);
                    return null;
                }
                let result = [];
                for (let i = 0; i < list.length; i++) {
                    let q = list[i];
                    let title = `第 ${i + 1} 题`;
                    if (q.type === 1 || q.type === 2) {
                        let blanks = q.blanks || [];
                        if (!blanks.length || blanks.length > MAX_BLANKS) {
                            this.$message.warning(`${title}配置不完整`);
                            return null;
                        }
                        for (let j = 0; j < blanks.length; j++) {
                            if (!(blanks[j].label || '').trim()) {
                                let msg = q.type === 1
                                    ? `请填写第 ${i + 1} 题第 ${j + 1} 个小题的标题`
                                    : `请填写第 ${i + 1} 题的标题`;
                                this.$message.warning(msg);
                                return null;
                            }
                        }
                        result.push({
                            ...(q.id != null ? { id: q.id } : {}),
                            type: q.type,
                            blanks: blanks.map((b) => {
                                let item = {
                                    ...(b.id != null ? { id: b.id } : {}),
                                    label: (b.label || '').trim(),
                                    hint: (b.hint || '').trim(),
                                };
                                if (q.type === 1) item.inputRule = Number(b.inputRule) === 1 ? 1 : 0;
                                return item;
                            }),
                        });
                    } else {
                        let options = q.options || [];
                        if (options.length < 2) {
                            this.$message.warning(`${title}至少需要 2 个选项`);
                            return null;
                        }
                        if (options.length > MAX_OPTIONS) {
                            this.$message.warning(`${title}最多 ${MAX_OPTIONS} 个选项`);
                            return null;
                        }
                        for (let k = 0; k < options.length; k++) {
                            if (!(options[k].text || '').trim()) {
                                this.$message.warning(`请填写${title}选项 ${this.optionLetter(k)}`);
                                return null;
                            }
                        }
                        result.push({
                            ...(q.id != null ? { id: q.id } : {}),
                            type: q.type,
                            prompt: (q.prompt || '').trim(),
                            options: options.map((o) => ({
                                ...(o.id != null ? { id: o.id } : {}),
                                text: (o.text || '').trim(),
                            })),
                        });
                    }
                }
                return result;
            },
            openQuestionTypeDialog() {
                if (this.readonly) return;
                if ((this.form.questions || []).length >= MAX_QUESTIONS) {
                    this.$message.warning(`最多添加 ${MAX_QUESTIONS} 道题目`);
                    return;
                }
                this.pendingQuestionType = 1;
                this.typeDialogVisible = true;
            },
            confirmAddQuestion() {
                if (this.readonly) return;
                if (!this.form.questions) this.$set(this.form, 'questions', []);
                this.form.questions.push(createQuestion(this.pendingQuestionType));
                this.typeDialogVisible = false;
                this.$message.success(`已添加「${this.questionTypeName(this.pendingQuestionType)}」`);
            },
            moveQuestion(index, delta) {
                if (this.readonly) return;
                let list = this.form.questions;
                let target = index + delta;
                if (target < 0 || target >= list.length) return;
                let current = list[index];
                this.$set(list, index, list[target]);
                this.$set(list, target, current);
            },
            removeQuestion(index) {
                if (this.readonly) return;
                if (this.form.questions.length <= 1) {
                    this.$message.warning('至少保留 1 道题目');
                    return;
                }
                this.form.questions.splice(index, 1);
            },
            addBlank(qIndex) {
                if (this.readonly) return;
                let q = this.form.questions[qIndex];
                if (!q || q.type !== 1) return;
                if (q.blanks.length >= MAX_BLANKS) {
                    this.$message.warning(`每题最多 ${MAX_BLANKS} 个小题`);
                    return;
                }
                q.blanks.push(emptyBlank());
            },
            removeBlank(qIndex, bIndex) {
                if (this.readonly) return;
                let q = this.form.questions[qIndex];
                if (!q || q.type !== 1) return;
                if (q.blanks.length <= 1) {
                    this.$message.warning('每题至少保留 1 个小题');
                    return;
                }
                q.blanks.splice(bIndex, 1);
            },
            addOption(qIndex) {
                if (this.readonly) return;
                let q = this.form.questions[qIndex];
                if (!q || (q.type !== 3 && q.type !== 4)) return;
                if (q.options.length >= MAX_OPTIONS) {
                    this.$message.warning(`每题最多 ${MAX_OPTIONS} 个选项`);
                    return;
                }
                q.options.push(emptyOption());
            },
            removeOption(qIndex, oIndex) {
                if (this.readonly) return;
                let q = this.form.questions[qIndex];
                if (!q || !q.options) return;
                if (q.options.length <= 2) {
                    this.$message.warning('至少保留 2 个选项');
                    return;
                }
                q.options.splice(oIndex, 1);
            },
            async saveDetail(goList) {
                let payload = this.buildPayload();
                if (!payload) return false;
                this.saving = true;
                try {
                    let res = this.editingId
                        ? await this.$Api.Log.updateTemplate(payload)
                        : await this.$Api.Log.createTemplate(payload);
                    if (isApiSuccess(res)) {
                        this.$message.success('保存成功');
                        this.takeSnapshot();
                        if (goList) this.backToList();
                        return true;
                    }
                    this.$message.error(apiMsg(res) || '保存失败');
                    return false;
                } catch (e) {
                    this.$message.error('保存失败');
                    return false;
                } finally {
                    this.saving = false;
                }
            },
            toggleStatus(item) {
                let next = item.status === 1 ? 0 : 1;
                if (next === 1) {
                    this.actionDialogData = {
                        question: '确定启用该日志设置？',
                        name: item.name || '未命名',
                        hint: '启用后，将对适用用户组生效。',
                        cancelText: '暂不启用',
                        confirmText: '确认启用',
                        loading: false,
                        callback: () => this.doToggleStatus(item, next),
                    };
                } else {
                    this.actionDialogData = {
                        question: '确定取消启用该日志设置？',
                        name: item.name || '未命名',
                        hint: '取消后，学员端将不再使用该设置。',
                        cancelText: '保持启用',
                        confirmText: '确认取消',
                        loading: false,
                        callback: () => this.doToggleStatus(item, next),
                    };
                }
                this.actionDialogVisible = true;
            },
            async doToggleStatus(item, next) {
                this.actionDialogData.loading = true;
                try {
                    let res = await this.$Api.Log.setTemplateStatus({ id: item.id, status: next });
                    if (isApiSuccess(res)) {
                        item.status = next;
                        this.$message.success(next === 1 ? '已启用' : '已取消启用');
                        this.actionDialogVisible = false;
                    } else {
                        this.$message.error(apiMsg(res) || '操作失败');
                    }
                } catch (e) {
                    this.$message.error('操作失败');
                } finally {
                    this.actionDialogData.loading = false;
                }
            },
            removeSetting(item) {
                this.actionDialogData = {
                    question: '确定删除该日志设置？',
                    name: item.name || '未命名',
                    hint: '删除后不可恢复。',
                    cancelText: '暂不删除',
                    confirmText: '确认删除',
                    loading: false,
                    callback: () => this.doRemoveSetting(item),
                };
                this.actionDialogVisible = true;
            },
            async doRemoveSetting(item) {
                this.actionDialogData.loading = true;
                try {
                    let res = await this.$Api.Log.deleteTemplate({ id: item.id });
                    if (isApiSuccess(res)) {
                        this.$message.success('已删除');
                        this.actionDialogVisible = false;
                        await this.fetchList();
                    } else {
                        this.$message.error(apiMsg(res) || '删除失败');
                    }
                } catch (e) {
                    this.$message.error('删除失败');
                } finally {
                    this.actionDialogData.loading = false;
                }
            },
            confirmActionDialog() {
                if (this.actionDialogData.callback) {
                    this.actionDialogData.callback();
                }
            },
        },
        mounted() {
            if (this.view === 'list') {
                this.$nextTick(() => this.syncListHeight());
            }
            window.addEventListener('resize', this._onResize = () => {
                if (this.view === 'list') this.syncListHeight();
            });
        },
        beforeDestroy() {
            if (this._onResize) window.removeEventListener('resize', this._onResize);
        },
    };
</script>

<style lang="less" scoped>
    .log-setting {
        padding: 20px 25px;
        box-sizing: border-box;
    }

    .list-view-root {
        display: flex;
        flex-direction: column;
    }

    .page-head {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        margin: 0 0 12px;
    }

    .setting-list {
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1;
        overflow-y: auto;
        min-height: 0;
    }

    .setting-item {
        background: #fff;
        border: 1px solid #EBEEF5;
        border-radius: 8px;
        padding: 14px 18px;
        display: flex;
        align-items: flex-start;
        gap: 14px;
        transition: border-color 0.2s, box-shadow 0.2s;

        &:hover {
            border-color: #D0D3D8;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }
    }

    .setting-item-left {
        flex: 1;
        min-width: 0;
    }

    .setting-item-title {
        font-size: 14px;
        font-weight: 600;
        color: #1F2A37;
        line-height: 22px;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .setting-item-desc {
        margin-top: 6px;
        font-size: 12px;
        font-weight: 400;
        color: #8B95A1;
        line-height: 20px;
    }

    .setting-item-right {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        flex-shrink: 0;
        gap: 6px;
    }

    .setting-item-actions {
        display: flex;
        gap: 8px;
        flex-shrink: 0;
        flex-wrap: wrap;
        justify-content: flex-end;
    }

    .setting-item-time {
        font-size: 12px;
        font-weight: 400;
        color: #B0B6BF;
        line-height: 20px;
        text-align: right;
    }

    .detail-back-bar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 16px;
    }

    .detail-back-left {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;
    }

    .detail-label {
        font-size: 13px;
        color: #909399;
    }

    .log-detail-shell {
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
        overflow: hidden;
    }

    .log-mode-card {
        padding: 20px 24px 12px;
    }

    .card-block {
        padding: 20px 24px;
        border-top: 1px solid #EBEEF5;
    }

    .card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 16px;

        h3 {
            margin: 0;
            font-size: 15px;
            font-weight: 600;
        }
    }

    .form-group {
        margin-bottom: 16px;
    }

    .form-label {
        display: block;
        margin-bottom: 8px;
        font-size: 14px;
        font-weight: 600;
        color: #303133;

        .required {
            color: #F56C6C;
            margin-left: 2px;
        }
    }

    .form-hint-inline {
        margin-left: 8px;
        font-size: 12px;
        font-weight: 400;
        color: #909399;
    }

    .form-row-2 {
        display: grid;
        grid-template-columns: minmax(0, 2fr) minmax(180px, 1fr);
        gap: 16px;
        align-items: start;
    }

    .time-range-row {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        gap: 8px;
        align-items: center;
    }

    .time-range-sep {
        color: #909399;
    }

    .switch-title-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
    }

    .date-summary-bar {
        margin: 0 0 10px;
        padding: 8px 12px;
        background: #F5F7FA;
        border-radius: 6px;
        font-size: 13px;
        color: #606266;
        line-height: 1.5;
    }

    .user-group-grid {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 8px;
    }

    .user-group-chip {
        display: inline-flex;
        align-items: center;
        padding: 7px 12px;
        border: 1px solid #E6E8EB;
        border-radius: 8px;
        font-size: 13px;
        color: #5C6673;
        cursor: pointer;
        background: #FAFBFC;
        user-select: none;
        transition: all 0.15s ease;

        input {
            position: absolute;
            opacity: 0;
            width: 0;
            height: 0;
            pointer-events: none;
        }

        &:hover:not(.is-disabled) {
            background: #fff;
            border-color: #C0C4CC;
        }

        &.is-checked {
            background: #F3F8FF;
            border-color: #B7D4F8;
            color: #3A7BD5;
            font-weight: 400;
        }

        &.is-disabled {
            cursor: not-allowed;
            opacity: 0.7;
        }
    }

    .daily-empty {
        padding: 32px 16px;
        text-align: center;
        color: #909399;
        font-size: 13px;
    }

    .day-setting-list {
        max-height: calc(56px * 3 + 226px);
        overflow-y: auto;
        border: 1px solid #EBEEF5;
        border-radius: 6px;
    }

    .day-setting-card {
        border-bottom: 1px solid #EBEEF5;

        &:last-child {
            border-bottom: none;
        }
    }

    .day-setting-header {
        display: flex;
        align-items: center;
        padding: 10px 16px;
        min-height: 56px;
        box-sizing: border-box;
        background: #fafafa;
        cursor: pointer;
        gap: 12px;

        &:hover {
            background: #f0f0f0;
        }
    }

    .day-num {
        width: 36px;
        height: 36px;
        border-radius: 8px;
        background: #409EFF;
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        font-weight: 700;
        flex-shrink: 0;
    }

    .day-info {
        flex: 1;
        min-width: 0;
    }

    .day-name {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
    }

    .day-meta {
        font-size: 12px;
        color: #909399;
        margin-top: 2px;
    }

    .day-toggle-icon {
        color: #909399;
        transition: transform 0.2s;

        &.is-open {
            transform: rotate(90deg);
        }
    }

    .day-setting-body {
        padding: 20px 16px 20px 64px;
        border-top: 1px solid #EBEEF5;
    }

    .log-mode-seg {
        display: inline-flex;
        padding: 3px;
        background: #F0F2F5;
        border-radius: 8px;
        gap: 2px;
    }

    /deep/ .el-tag.is-multi {
        background: #F3E8FF;
        border-color: #E4D4F7;
        color: #7C3AED;
    }

    .multi-q-toolbar {
        margin-bottom: 12px;
    }

    .multi-q-hint {
        font-size: 13px;
        color: #909399;
        line-height: 1.5;
        font-weight: 400;
    }

    .multi-q-list {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }

    .multi-q-card {
        border: 1px solid #EBEEF5;
        border-radius: 6px;
        background: #FAFBFC;
        overflow: hidden;
    }

    .multi-q-card-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        padding: 10px 14px;
        background: #F5F7FA;
        border-bottom: 1px solid #EBEEF5;
    }

    .multi-q-head-left {
        display: flex;
        align-items: center;
        min-width: 0;
    }

    .multi-q-index {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 22px;
        height: 22px;
        padding: 0 8px;
        border-radius: 11px;
        background: #ECF5FF;
        color: #409EFF;
        font-size: 12px;
        font-weight: 600;
    }

    .multi-q-type-tag {
        display: inline-flex;
        align-items: center;
        padding: 1px 8px;
        border-radius: 10px;
        font-size: 12px;
        font-weight: 500;
        background: #ECF5FF;
        color: #409EFF;
        margin-left: 8px;
    }

    .multi-q-actions,
    .multi-blank-actions {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-shrink: 0;
    }

    .multi-blank-actions {
        align-self: end;
        padding-bottom: 2px;
    }

    .q-icon-btn {
        width: 28px;
        height: 28px;
        padding: 0;
        border: 1px solid #DCDFE6;
        border-radius: 4px;
        background: #fff;
        color: #909399;
        cursor: pointer;
        font-size: 13px;
        line-height: 1;
        font-family: inherit;

        &:hover:not(:disabled) {
            color: #409EFF;
            border-color: #409EFF;
            background: #ECF5FF;
        }

        &.is-danger:hover:not(:disabled) {
            color: #F56C6C;
            border-color: #F56C6C;
            background: #FEF0F0;
        }

        &:disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
    }

    .multi-q-card-body {
        padding: 14px;
    }

    .multi-blank-list,
    .multi-option-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .multi-option-list {
        gap: 8px;
    }

    .multi-blank-row {
        padding: 12px;
        background: #fff;
        border: 1px solid #EBEEF5;
        border-radius: 6px;

        &.is-indexed {
            position: relative;
            padding-left: 42px;

            > .form-group {
                margin-bottom: 0;
            }
        }
    }

    .multi-blank-index {
        position: absolute;
        left: 12px;
        bottom: 12px;
        line-height: 40px;
        color: #303133;
        font-size: 13px;
        font-weight: 600;
    }

    .multi-blank-fields {
        display: grid;
        grid-template-columns: minmax(120px, 1fr) minmax(160px, 1.3fr) minmax(140px, 0.9fr) auto;
        gap: 10px;
        align-items: end;

        .form-group {
            margin-bottom: 0;
        }

        &.is-essay {
            grid-template-columns: minmax(140px, 1fr) minmax(180px, 1.4fr);
        }
    }

    .multi-option-row {
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 8px;
        align-items: center;
    }

    .multi-option-index {
        font-size: 13px;
        color: #909399;
        min-width: 22px;
    }

    .multi-q-add-line,
    .multi-q-add-wrap {
        margin-top: 12px;
    }

    .q-type-hint {
        margin-bottom: 12px;
        font-size: 13px;
        color: #909399;
        line-height: 1.5;
    }

    .q-type-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
    }

    .q-type-option {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding: 14px 12px;
        border: 1px solid #DCDFE6;
        border-radius: 8px;
        background: #fff;
        cursor: pointer;
        text-align: left;
        font-family: inherit;

        &:hover,
        &.is-selected {
            border-color: #409EFF;
            background: #ECF5FF;
        }

        &.is-selected {
            box-shadow: 0 0 0 1px #409EFF;
        }
    }

    .q-type-name {
        font-size: 14px;
        font-weight: 600;
        color: #303133;
    }

    .q-type-desc {
        font-size: 12px;
        color: #909399;
        line-height: 1.45;
    }

    .log-mode-btn {
        padding: 9px 18px;
        border: none;
        border-radius: 6px;
        background: transparent;
        font-size: 13px;
        font-weight: 500;
        color: #606266;
        cursor: pointer;
        font-family: inherit;

        &.is-active {
            background: #fff;
            color: #409EFF;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        }

        &:disabled {
            cursor: not-allowed;
        }
    }

    .leave-dialog-body {
        font-size: 14px;
        color: #303133;
        line-height: 1.6;
        padding: 4px 0 8px;
    }

    .action-dialog-body {
        text-align: center;
        padding: 12px 0 4px;
    }

    .action-dialog-question {
        font-size: 16px;
        font-weight: 600;
        color: #1F2A37;
        margin-bottom: 12px;
    }

    .action-dialog-name {
        font-size: 15px;
        color: #3A7BD5;
        margin-bottom: 8px;
    }

    .action-dialog-hint {
        font-size: 13px;
        color: #8B95A1;
    }

    /deep/ .el-input__inner,
    /deep/ .el-textarea__inner {
        border-radius: 6px;
    }
</style>

<style lang="less">
    .log-leave-dialog {
        border-radius: 8px;
        overflow: hidden;

        .el-dialog__header {
            padding: 16px 20px 8px;
        }

        .el-dialog__body {
            padding: 8px 20px 12px;
        }

        .el-dialog__footer {
            padding: 8px 20px 16px;
        }
    }

    .log-q-type-dialog {
        border-radius: 8px;

        .el-dialog__body {
            padding: 8px 20px 16px;
        }
    }

    .log-action-dialog {
        border-radius: 12px;
        overflow: hidden;

        .el-dialog__header {
            padding: 12px 16px 0;

            .el-dialog__headerbtn {
                top: 12px;
                right: 12px;
            }
        }

        .el-dialog__body {
            padding: 8px 24px 16px;
        }

        .el-dialog__footer {
            padding: 8px 24px 20px;
            text-align: center;
        }
    }
</style>
